/**
 * BottomNav Component
 * 
 * Main bottom navigation with Home, Clubs, Events, You tabs and reserved "O" slot.
 * Uses shared navigation config for consistency with other nav components.
 * 
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

import { useState } from 'react';
import { NavLink } from "react-router-dom";
import { MAIN_NAV_ITEMS, filterVisibleItems } from '../config/navigationConfig';
import { getZIndexClass } from '../config/zIndex';

// Split navigation items into left and right groups for the reserved O slot
const visibleNavItems = filterVisibleItems(MAIN_NAV_ITEMS);
const leftItems = visibleNavItems.slice(0, 2); // Home, Clubs
const rightItems = visibleNavItems.slice(2); // Events, You

export function BottomNav() {
  const [showDebugOverlay, setShowDebugOverlay] = useState(false);

  return (
    <>
      <nav 
        className={`fixed bottom-0 left-0 right-0 ${getZIndexClass('bottomNav')} rounded-t-2xl md:hidden`}
      style={{
        backgroundColor: 'var(--oro-surface-alt)',
        borderTop: '1px solid var(--oro-border)',
        boxShadow: 'var(--shadow-floating)',
        padding: '0.75rem 0',
        // Ensure fixed positioning works on iOS Safari
        position: 'fixed',
        willChange: 'transform',
        paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0))',
      }}
    >
      <div className="flex items-center justify-around max-w-md mx-auto">
        {leftItems.map((item) => (
          <BottomNavItem key={item.path} {...item} />
        ))}
        
        <ReservedOSlot 
          showDebugOverlay={showDebugOverlay}
          setShowDebugOverlay={setShowDebugOverlay}
        />
        
        {rightItems.map((item) => (
          <BottomNavItem key={item.path} {...item} />
        ))}
      </div>
      </nav>
      
      {showDebugOverlay && (
        <DebugOverlay onClose={() => setShowDebugOverlay(false)} />
      )}
    </>
  );
}

function BottomNavItem({ path, label, icon: Icon }: typeof MAIN_NAV_ITEMS[0]) {
  return (
    <NavLink
      to={path}
      className="flex flex-col items-center justify-center gap-1 py-2 px-3 transition-colors"
      style={({ isActive }) => ({
        color: isActive ? 'var(--oro-gold)' : 'var(--text-muted)'
      })}
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[11px] font-medium">
        {label}
      </span>
    </NavLink>
  );
}

function ReservedOSlot({ 
  setShowDebugOverlay 
}: { 
  showDebugOverlay: boolean;
  setShowDebugOverlay: (show: boolean) => void;
}) {
  const [tapCount, setTapCount] = useState(0);

  const handleLogoClick = () => {
    const next = tapCount + 1;
    setTapCount(next);

    if (next === 3) {
      setShowDebugOverlay(true);
      setTapCount(0);
    } else {
      // Reset tap count after 1.5 seconds of inactivity
      setTimeout(() => setTapCount(0), 1500);
    }
  };

  return (
    <div className="flex items-center justify-center px-4">
      <button 
        className="relative flex items-center justify-center"
        onClick={handleLogoClick}
        type="button"
        aria-label="ORO Debug (Triple-tap to open debug info)"
      >
        <div className="oro-center-button-breathe w-[52px] h-[52px] flex items-center justify-center">
          <div className="oro-center-button-inner w-[44px] h-[44px] flex items-center justify-center shadow-[var(--shadow-card)]">
            {/* ORO Mark - Circular O Badge */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="opacity-85"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="var(--oro-gold)"
                strokeWidth="2.5"
                fill="none"
              />
            </svg>
          </div>
        </div>
      </button>
    </div>
  );
}

function DebugOverlay({ onClose }: { onClose: () => void }) {
  // Get environment info
  const buildNumber = import.meta.env.VITE_BUILD_NUMBER || 'dev';
  const gitSha = import.meta.env.VITE_GIT_SHA || 'unknown';
  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development';
  const version = import.meta.env.VITE_APP_VERSION || 'v0.9.0';

  return (
    <div 
      className={`fixed inset-0 ${getZIndexClass('overlay')} flex items-center justify-center p-4`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={onClose}
    >
      <div 
        className="max-w-sm w-full space-y-4 p-6 rounded-lg"
        style={{
          backgroundColor: 'var(--oro-surface)',
          border: '1px solid var(--oro-border)',
          boxShadow: 'var(--shadow-floating)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Debug Info
        </h2>
        
        <dl className="text-sm space-y-3">
          <div className="flex justify-between">
            <dt style={{ color: 'var(--text-secondary)' }}>Version</dt>
            <dd style={{ color: 'var(--text-primary)' }}>{version}</dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--text-secondary)' }}>Build</dt>
            <dd style={{ color: 'var(--text-primary)' }}>#{buildNumber}</dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--text-secondary)' }}>Commit</dt>
            <dd style={{ color: 'var(--text-primary)' }} className="font-mono text-xs">
              {gitSha}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--text-secondary)' }}>Environment</dt>
            <dd style={{ color: 'var(--text-primary)' }}>
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                environment === 'production' ? 'bg-green-100 text-green-800' :
                environment === 'staging' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {environment}
              </span>
            </dd>
          </div>
        </dl>

        <div className="pt-4 border-t" style={{ borderColor: 'var(--oro-border)' }}>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 rounded-lg font-medium transition-colors"
            style={{
              backgroundColor: 'var(--oro-surface-alt)',
              color: 'var(--text-primary)',
              border: '1px solid var(--oro-border)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--oro-border)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--oro-surface-alt)';
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

